import {
  Fragment,
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'
import { useQueryClient } from 'react-query'

import { Button } from '@wartek-id/button-v1'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import { SKP_QUERY_KEY, useAPIUpdateState } from 'api/kinerja/useAPIKinerjaKS'
import PanelKinerja from 'app/KinerjaKSPage/components/PanelKinerja'
import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import { mapKuantitas } from 'app/KinerjaKSPage/utils/kinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import {
  PanelKinerjaSourceTabId,
  type KinerjaKsPerencanaanTabBaseProps,
} from 'types/kinerja-ks'
import TabRangkumanReady from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman/Ready'
import DialogFinalizeSKP from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman/components/DialogFinalizeSKP'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { useApiSyncSkpPerencanaan } from 'api/kinerja/pengaliran-data-ekin'
import { assetUrl } from 'utils/url'
import { SubmitConfirmationDialog } from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman/components/SubmitConfirmationDialog'
import { API_GET_SCHOOLS_QUERY_KEY } from 'api/kinerja/kepala-sekolah/school'
import { API_GET_KS_PELAKSANAAN_QUERY_KEY } from 'api/kinerja/kepala-sekolah/pelaksanaan'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'

import type { ForwardedRef } from 'react'
import type { PerilakuKerjaPayload, SKPTabHandler } from 'types/kinerja'
import type { PanelKinerjaProps } from 'app/KinerjaKSPage/components/PanelKinerja'

interface PerencanaanTabRangkumanProps
  extends KinerjaKsPerencanaanTabBaseProps {
  onRequestChange?: () => void
  onClickBackToHome?: () => void
  onClickSave?: () => void
  hideDetails?: boolean
}

type RencanaHasilKinerjaItem = PanelKinerjaProps['kinerja'] & {
  label: string
}

const TabRangkuman = (
  {
    skp,
    isSubmitting,
    onRequestChange = () => {},
    onClickBackToHome = () => {},
    onClickSave = () => {},
    hideDetails = false,
  }: PerencanaanTabRangkumanProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const queryClient = useQueryClient()
  const { periodYear } = usePeriodContext()
  const { trackClickProposePlanning, trackClickProposePlanningConfirmation } =
    useSKPKSTrackEvent()
  const { isKepalaSkb } = useRoleContext()

  const {
    contentPengembanganKompetensi,
    contentPerilakuKerja,
    contentRencanaHasilKerjaEkstra,
    getKepemimpinanSatuanPendidikanById,
  } = useContentContext()
  const toast = useToast()
  const { isDemoMode } = useDemoContext()

  const getPerilakuKerjaRow = (value: PerilakuKerjaPayload) => {
    const aspek = contentPerilakuKerja.aspek.find(
      ({ id }) => value.aspekId === id
    )
    const indikator = aspek?.indikator.find(
      ({ id }) => id === value.indikatorId
    )

    const ekspektasi = indikator?.ekspektasi.find(
      ({ id }) => id === value.ekspektasiId
    )

    return {
      aspek: aspek?.value ?? '',
      indikator: indikator?.value ?? '',
      ekspektasi: ekspektasi?.value ?? '',
    }
  }

  const [isOpenDialogFinalize, setIsOpenDialogFinalize] = useState(false)

  const {
    mutateAsync: finalizeSKP,
    isLoading: isFinalizingSKP,
    isSuccess: isSuccessFinalize,
  } = useAPIUpdateState({ isDemoMode })

  const praktikKinerja: Array<RencanaHasilKinerjaItem> = useMemo(() => {
    return skp.kepemimpinanSatuanPendidikan.map((id) => {
      return {
        ...getKepemimpinanSatuanPendidikanById(id),
        sourceTabId: PanelKinerjaSourceTabId.PraktikKinerjaKepalaSekolah,
        label: 'Rencana Hasil Kerja',
      }
    })
  }, [skp.kepemimpinanSatuanPendidikan, getKepemimpinanSatuanPendidikanById])

  const pengembanganKompetensi: Array<RencanaHasilKinerjaItem> = useMemo(() => {
    return skp.pengembanganKompetensi
      .filter((k) => k.id > 0) // Only include the valid ones
      .map((kinerja) => {
        const selectedPengembanganKompetensi = mapKuantitas(
          contentPengembanganKompetensi,
          kinerja
        )

        const title =
          periodYear >= 2026
            ? `Terlaksananya pengembangan kompetensi ${
                isKepalaSkb ? 'Kepala SKB' : 'Kepala Sekolah'
              } sebagai pemimpin pembelajaran yang berdampak pada pendidikan yang bermutu untuk semua berdasarkan indikator ${
                selectedPengembanganKompetensi?.rencanaHasilKerja
              }`
            : selectedPengembanganKompetensi?.rencanaHasilKerja

        return {
          ...selectedPengembanganKompetensi,
          rencanaHasilKerja: title,
          bobot: selectedPengembanganKompetensi?.bobot || 0 * kinerja.kuantitas,
          sourceTabId: PanelKinerjaSourceTabId.PengembanganKompetensi,
          label: 'Indikator Kompetensi',
        }
      })
  }, [
    skp.pengembanganKompetensi,
    contentPengembanganKompetensi,
    periodYear,
    isKepalaSkb,
  ])

  const rencanaHasilKerja: Array<RencanaHasilKinerjaItem> = useMemo(() => {
    const topRhk: Array<RencanaHasilKinerjaItem> =
      contentRencanaHasilKerjaEkstra
        ?.filter((v) => !!v?.position?.placeOnTop)
        ?.sort((a, b) => {
          const sortingA = isNaN(a.position.order) ? 0 : a.position.order
          const sortingB = isNaN(b.position.order) ? 0 : b.position.order
          return sortingA - sortingB
        })
        .map((v) => {
          return {
            ...v,
            sourceTabId: PanelKinerjaSourceTabId.RencanaHasilKerjaEkstra,
            label: 'Rencana Hasil Kerja',
          }
        })

    const bottomRhk: Array<RencanaHasilKinerjaItem> =
      contentRencanaHasilKerjaEkstra
        ?.filter((v) => !v?.position?.placeOnTop)
        ?.sort((a, b) => {
          const sortingA = isNaN(a.position.order) ? 0 : a.position.order
          const sortingB = isNaN(b.position.order) ? 0 : b.position.order
          return sortingA - sortingB
        })
        .map((v) => {
          return {
            ...v,
            sourceTabId: PanelKinerjaSourceTabId.RencanaHasilKerjaEkstra,
            label: 'Rencana Hasil Kerja',
          }
        })

    if (periodYear >= 2026) {
      return topRhk.concat(bottomRhk)
    } else {
      return topRhk.concat(praktikKinerja, pengembanganKompetensi, bottomRhk)
    }
  }, [
    periodYear,
    praktikKinerja,
    pengembanganKompetensi,
    contentRencanaHasilKerjaEkstra,
  ])

  const { mutateAsync: syncSkpToBkn } = useApiSyncSkpPerencanaan()
  const handleClickFinalize = async () => {
    trackClickProposePlanning({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGAJUAN_POP_UP',
    })

    try {
      await finalizeSKP(
        {
          skpId: skp.id,
        },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(API_GET_SCHOOLS_QUERY_KEY)
            queryClient.invalidateQueries(API_GET_KS_PELAKSANAAN_QUERY_KEY)
          },
        }
      )

      if (skp.ekinEligible) {
        // Due to complexity in the BE side, we need to sync SKP to BKN
        // manually after finalizing the SKP instead of automatically
        // syncing it in the BE side. We ignore the result of this
        // operation since it is not critical to the user experience.
        await syncSkpToBkn({
          year: skp.tahun,
          force: 0,
        }).catch((e) => {
          console.error('Failed to sync SKP to BKN:', e)
        })
      }

      setIsOpenDialogFinalize(false)
    } catch (e) {
      console.error(e)

      toast({
        message: 'Gagal dikumpulkan. Silakan coba lagi.',
      })
    }
  }

  const onSaveDraft = useCallback(() => {
    onClickSave()
  }, [onClickSave])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  // Invalidate caches related to SKP data to make sure the app shows the latest data after user finalize the SKP
  // upon unmount
  useEffect(() => {
    return () => {
      queryClient.invalidateQueries(SKP_QUERY_KEY)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (isSuccessFinalize) {
    return (
      <TabRangkumanReady
        tipe={skp?.tipe}
        onClickBackToHome={onClickBackToHome}
      />
    )
  }

  return (
    <div>
      <div className="px-6 space-y-10 py-9">
        <div className="space-y-1 mb-9">
          <Text variant="heading-md" as="h3">
            Rangkuman Perencanaan Kinerja Anda
          </Text>
          <ul className="pl-5 list-disc list-outside">
            <Text as="li">
              Anda dapat cek ulang dan ubah sebelum mengirim ke Pejabat Penilai
              Kinerja.
            </Text>
            <Text as="li">
              Format kalimat Perencanaan Kinerja sudah kami sesuaikan dengan
              aturan yang berlaku. Sehingga, terdapat perencanaan (
              <strong>Hasil Kerja Utama</strong>) yang otomatis ditambahkan
              selain yang Anda pilih. Mohon dicek, ya.
            </Text>
          </ul>
        </div>

        <section>
          {periodYear >= 2025 ? (
            <div className="flex items-center gap-3 mb-4">
              <img
                src={assetUrl('/illustrations/img-badge-star-ribbon-pro.png')}
                className="flex-grow-0 flex-shrink-0"
                alt=""
                width={52}
                height={52}
              />
              <Text variant="heading-sm" as="h4">
                Hasil Kerja Utama
              </Text>
            </div>
          ) : (
            <Text as="h2" variant="heading-sm" className="mb-4">
              Hasil Kerja Utama
            </Text>
          )}

          <div className="flex flex-col gap-4">
            {rencanaHasilKerja.map((item, index) => {
              return (
                <Fragment key={index}>
                  <PanelKinerja
                    hideDetails={hideDetails}
                    label={item.label}
                    kinerja={item}
                    number={index + 1}
                  />
                </Fragment>
              )
            })}
          </div>
        </section>

        {periodYear >= 2025 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={assetUrl('/illustrations/img-badge-star.png')}
                className="flex-grow-0 flex-shrink-0"
                alt=""
                width={52}
                height={52}
              />
              <Text variant="heading-sm">Praktik Kinerja</Text>
            </div>

            <div className="space-y-4">
              {praktikKinerja.map((item, index) => {
                return (
                  <Fragment key={index}>
                    <PanelKinerja
                      hideDetails={hideDetails}
                      label={item.label}
                      kinerja={item}
                      number={index + 1}
                    />
                  </Fragment>
                )
              })}
            </div>
          </section>
        )}

        {periodYear >= 2025 && (
          <section>
            <div className="flex items-center gap-3 mb-4">
              <img
                src={assetUrl('/illustrations/img-document.png')}
                className="flex-grow-0 flex-shrink-0"
                alt=""
                width={52}
                height={52}
              />
              <Text variant="heading-sm">Pengembangan Kompetensi</Text>
            </div>
            <div className="space-y-4">
              {pengembanganKompetensi.map((item, index) => {
                return (
                  <Fragment key={index}>
                    <PanelKinerja
                      hideDetails={hideDetails}
                      label={item.label}
                      kinerja={item}
                      number={index + 1}
                    />
                  </Fragment>
                )
              })}
            </div>
          </section>
        )}

        <section>
          <Text as="h4" variant="heading-sm" className="mb-4">
            Perilaku Kerja
          </Text>

          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>No.</TableHeaderItem>
              <TableHeaderItem>Aspek</TableHeaderItem>
              <TableHeaderItem>Indikator Perilaku</TableHeaderItem>
              <TableHeaderItem>Ekspektasi Khusus Pimpinan</TableHeaderItem>
            </TableHeader>
            <TableBody>
              {skp?.perilakuKerja
                ?.map(getPerilakuKerjaRow)
                ?.map((perilakuKerja, idx) => (
                  <TableRow key={`perilaku-kerja-${idx}`}>
                    <TableRowItem>{idx + 1}</TableRowItem>
                    <TableRowItem>{perilakuKerja.aspek}</TableRowItem>
                    <TableRowItem>{perilakuKerja.indikator}</TableRowItem>
                    <TableRowItem>{perilakuKerja.ekspektasi}</TableRowItem>
                  </TableRow>
                ))}
            </TableBody>
          </TablePelaksanaanKinerja>
        </section>
      </div>

      <Separator />

      <div className="flex justify-end gap-5 px-8 py-4">
        <Button size="lg" color="white" onClick={onRequestChange}>
          Ubah
        </Button>
        <Button
          size="lg"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          type="submit"
          data-testid="btn-next"
          onClick={() => {
            trackClickProposePlanningConfirmation({
              kinerjaPlanningId: skp.id,
              kinerjaPlanningStatus: skp.status,
              screenName: 'KINERJA_PLANNING_KS_RANGKUMAN_PAGE',
            })

            setIsOpenDialogFinalize(true)
          }}
        >
          {periodYear >= 2025 ? 'Kirim Perencanaan' : 'Kumpulkan'}
        </Button>

        {periodYear >= 2025 ? (
          <SubmitConfirmationDialog
            isOpen={isOpenDialogFinalize}
            isLoading={isFinalizingSKP}
            onCancel={() => setIsOpenDialogFinalize(false)}
            onClose={() => setIsOpenDialogFinalize(false)}
            onConfirm={handleClickFinalize}
          />
        ) : (
          <DialogFinalizeSKP
            data-testid="dialog-finalize-skp"
            isOpen={isOpenDialogFinalize}
            onClose={() => setIsOpenDialogFinalize(false)}
            isFinalizing={isFinalizingSKP}
            onClickCancel={() => setIsOpenDialogFinalize(false)}
            onClickFinalize={handleClickFinalize}
            ekinEligible={skp.ekinEligible}
          />
        )}
      </div>
    </div>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabRangkumanProps>(
  TabRangkuman
)
